import { Dispatch, SetStateAction, useCallback, useState } from "react";

type SetMutator<T> = (set: Set<T>) => boolean;
type SetHookResult<T> = [
  Set<T>,
  (mutator: SetMutator<T>) => void,
  (item: T, included?: boolean) => void,
  Dispatch<SetStateAction<Set<T>>>
];
/**
 * This is a hook used for working comfortably with sets.
 *
 * @param setCreator a method used to create an initial set
 * @returns set itself, mutate function, toggleItem function, and replaceSet function
 */
const useSet = <T>(setCreator?: () => Set<T>): SetHookResult<T> => {
  const [set, replaceSet] = useState<Set<T>>(setCreator ? setCreator() : new Set<T>());
  const mutateSet = useCallback(
    (mutator: SetMutator<T>) =>
      replaceSet((currentSet) => {
        const newSet = new Set(currentSet);
        return mutator(newSet) ? newSet : currentSet; // keep current set if mutator changed nothing
      }),
    [replaceSet]
  );
  const toggleItem = useCallback(
    (item: T, included?: boolean) =>
      mutateSet((set: Set<T>) => {
        if (included === undefined) {
          included = !set.has(item);
        }
        const setChanged = included ? !set.has(item) : set.has(item);
        if (setChanged && included) {
          set.add(item);
        } else if (setChanged) {
          set.delete(item);
        }
        return setChanged;
      }),
    [mutateSet]
  );
  return [set, mutateSet, toggleItem, replaceSet];
};

export default useSet;
